import { QueryHookOptions } from '@apollo/client';
import React from 'react';
import { OperationData, OperationVariables, gql } from '@ts-gql/tag';
import { useQuery } from '@ts-gql/apollo';

import { parseSearchJobsVariables } from '../utils/data-utils';
import {
  useTenantTimezoneConversion,
  utcToTenantToLocalDateFunction,
} from '../hooks/use-tenant-timezone';

export const SEARCH_JOBS_QUERY = gql`
  query SearchJobs(
    $clients: [String!] = []
    $myWork: Boolean = false
    $staffIds: [String!] = []
    $managerIds: [String!] = []
    $partnerIds: [String!] = []
    $statuses: [Float!] = []
    $dueDateBegin: DateTime = null
    $dueDateEnd: DateTime = null
    $dueDateOverdue: Boolean = false
    $startDateBegin: DateTime = null
    $startDateEnd: DateTime = null
    $closedDateBegin: DateTime = null
    $closedDateEnd: DateTime = null
    $completedDateBegin: DateTime = null
    $completedDateEnd: DateTime = null
    $familyGroupIds: [String!] = []
    $templateIds: [String!] = []
    $text: String = null
    $take: Float = null
    $statusIds: [String!] = []
    $assigneeIds: [String!] = []
  ) {
    searchJobs(
      clients: $clients
      myWork: $myWork
      staffIds: $staffIds
      managerIds: $managerIds
      partnerIds: $partnerIds
      statuses: $statuses
      dueDateBegin: $dueDateBegin
      dueDateEnd: $dueDateEnd
      dueDateOverdue: $dueDateOverdue
      startDateBegin: $startDateBegin
      startDateEnd: $startDateEnd
      closedDateBegin: $closedDateBegin
      closedDateEnd: $closedDateEnd
      completedDateBegin: $completedDateBegin
      completedDateEnd: $completedDateEnd
      familyGroupIds: $familyGroupIds
      templateIds: $templateIds
      text: $text
      take: $take
      statusIds: $statusIds
      assigneeIds: $assigneeIds
    ) {
      ...SearchJobs_Job
    }
  }
` as import('../../__generated__/ts-gql/SearchJobs').type;

export const SearchJobsFragment = gql`
  fragment SearchJobs_Job on Job {
    cloudId
    tenantId
    name
    clientId
    staffUserId
    managerUserId
    partnerUserId
    assigneeUserId
    jobStatusId
    dueDate
    completedDate
    closedDate
    budget
    signOffStatus
    WfDeletedOnUTC
    startDate
  }
` as import('../../__generated__/ts-gql/SearchJobs_Job').type;

export type SearchJobsQueryVariables = OperationVariables<
  typeof SEARCH_JOBS_QUERY
>;
export type SearchJobsQueryResult = OperationData<typeof SEARCH_JOBS_QUERY>;

export type SearchJobResult = NonNullable<
  ReturnType<typeof processData>
>[number];

export const useSearchJobs = (
  options: QueryHookOptions<SearchJobsQueryResult, SearchJobsQueryVariables>
) => {
  const variables = options.variables as SearchJobsQueryVariables;

  const { localDateToTenantToUtc, utcToTenantToLocalDate, loading } =
    useTenantTimezoneConversion();

  const res = useQuery(SEARCH_JOBS_QUERY, {
    context: { service: 'core' },
    ...options,
    variables: parseSearchJobsVariables(variables, localDateToTenantToUtc),
    skip: options.skip || loading,
  });

  return React.useMemo(() => {
    if (loading) {
      return {
        loading: true,
        data: undefined,
        error: undefined,
        refetch: undefined,
      };
    }
    return {
      ...res,
      data: processData(res.data, utcToTenantToLocalDate),
    };
  }, [res, utcToTenantToLocalDate, loading]);
};

export function processData(
  data: SearchJobsQueryResult | undefined,
  dateConvert: utcToTenantToLocalDateFunction
) {
  const raw = data?.searchJobs;
  const results =
    raw &&
    raw.map((job) => ({
      ...job,
      dueDate: job.dueDate && dateConvert(job.dueDate),
      completedDate: job.completedDate && new Date(job.completedDate),
      closedDate: job.closedDate && new Date(job.closedDate),
      WfDeletedOnUTC: job.WfDeletedOnUTC && new Date(job.WfDeletedOnUTC),
      startDate: job.startDate && dateConvert(job.startDate),
    }));
  return results;
}
